import type { CSSProperties } from "react";
import { h5BackButtonCssVars } from "../components/common/H5BackButtonConfig";

/** 登录页：顶部导航 */
export interface LoginPageTopBarConfig {
  /** 返回按钮距屏幕顶部（px，不含 safe-area；safe-area 会叠加） */
  offsetTop: number;
}

/** 登录页：按位数字输入框 */
export interface LoginPageInputConfig {
  /** 输入框整体高度（px） */
  height: number;
  /** 输入数字字号（px） */
  fontSize: number;
  /** +86 前缀字号（px），独立于输入数字 */
  prefixFontSize: number;
  /** placeholder 字号（px），独立于输入数字 */
  placeholderFontSize: number;
  /** 数字按位间距（px，仅内部展示，无外框） */
  digitGap: number;
  /** 输入格圆角（px） */
  borderRadius: number;
  /**
   * 框内字体（+86、placeholder、手机号、验证码共用）。
   * 默认 inherit，即页面系统字体。
   */
  fontFamily: string;
}

/** 登录页：主内容区 */
export interface LoginPageBodyConfig {
  /**
   * 「手机号登录/注册」标题距屏幕顶部的比例（相对 100dvh，如 18 表示 18%）。
   * safe-area 会叠加在该比例之上。
   */
  titleOffsetTopPercent: number;
  /** 标题字号（px） */
  titleFontSize: number;
  /** 表单区左右内边距（px） */
  paddingX: number;
  /** 表单区最大宽度（px） */
  maxWidth: number;
}

/** 登录页：主题色（与 Web 登录框一致） */
export interface LoginPageThemeConfig {
  accent: string;
  accentHover: string;
  accentActive: string;
}

export const LOGIN_PAGE_LAYOUT = {
  topBar: {
    offsetTop: 30,
  },
  body: {
    titleOffsetTopPercent: 50,
    titleFontSize: 32,
    paddingX: 24,
    maxWidth: 400,
  },
  input: {
    /** 手机号 / 验证码输入框整体高度（px） */
    height: 48,
    /** 输入数字字号 */
    fontSize: 20,
    /** +86 前缀字号 */
    prefixFontSize: 16,
    /** placeholder 字号 */
    placeholderFontSize: 16,
    digitGap: 4,
    borderRadius: 4,
    fontFamily: "inherit",
  },
  theme: {
    accent: "#e91e8c",
    accentHover: "#db2777",
    accentActive: "#be185d",
  },
} as const satisfies {
  topBar: LoginPageTopBarConfig;
  body: LoginPageBodyConfig;
  input: LoginPageInputConfig;
  theme: LoginPageThemeConfig;
};

/** 登录页布局 CSS 变量（注入到 `.login-page-root`） */
export function loginPageLayoutCssVars(): CSSProperties {
  const { topBar, body, input, theme } = LOGIN_PAGE_LAYOUT;
  return {
    ...h5BackButtonCssVars(),
    ["--login-back-offset-top" as string]: `${topBar.offsetTop}px`,
    ["--login-title-offset-top-percent" as string]: `${body.titleOffsetTopPercent}%`,
    ["--login-title-font-size" as string]: `${body.titleFontSize}px`,
    ["--login-body-padding-x" as string]: `${body.paddingX}px`,
    ["--login-body-max-width" as string]: `${body.maxWidth}px`,
    ["--login-digit-input-height" as string]: `${input.height}px`,
    ["--login-input-font-size" as string]: `${input.fontSize}px`,
    ["--login-input-prefix-font-size" as string]: `${input.prefixFontSize}px`,
    ["--login-input-placeholder-font-size" as string]: `${input.placeholderFontSize}px`,
    ["--login-input-font-family" as string]: input.fontFamily,
    ["--login-digit-gap" as string]: `${input.digitGap}px`,
    ["--login-digit-border-radius" as string]: `${input.borderRadius}px`,
    ["--login-accent" as string]: theme.accent,
    ["--login-accent-hover" as string]: theme.accentHover,
    ["--login-accent-active" as string]: theme.accentActive,
  };
}
